import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from './useProducts';
import CatalogImage from './catalog/CatalogImage';
import { formatProductTitle } from './formatProductTitle';
import HomeDynamicCatalog from './HomeDynamicCatalog';
import HomeExhibitions from './HomeExhibitions';
import HomeNextExhibitionBanner from './HomeNextExhibitionBanner';
import { useExhibitions } from './useExhibitions';
import HomeAnnouncementBar from './HomeAnnouncementBar';
import { useSiteSettings } from './SiteSettingsContext';
import PageSeoHelmet from './PageSeoHelmet';
import StorefrontHeroActions from './components/StorefrontHeroActions';
import { SITE_SETTINGS_DEFAULTS } from './siteSettingsDefaults';
import { mergeBlogContent } from './content/siteCopyHelpers';
import InstagramEmbed from './components/InstagramEmbed';

const Home = () => {
  const { categories, allProducts, loading } = useProducts();
  const exhibitions = useExhibitions();
  const { settings: site } = useSiteSettings();

  const blogContent = useMemo(() => mergeBlogContent(site.blog_content), [site.blog_content]);
  const homeCopy = site.page_copy?.home || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const blogStories = blogContent.posts.map((p) => ({
    title: p.title,
    excerpt: p.excerpt,
    link: `/blog/${p.slug}`,
  }));

  const instagramPosts = useMemo(() => {
    return (allProducts || [])
      .filter((p) => String(p?.image || '').trim())
      .slice(0, 4)
      .map((p) => ({
        key: `${p.category}-${p.id}`,
        image: String(p.image).trim(),
        caption: formatProductTitle(p.name || ''),
      }));
  }, [allProducts]);

  return (
    <div className="Home-main">
    <PageSeoHelmet page="home" />

    <div className="home-top-strips">
      {exhibitions.status === 'ready' && exhibitions.upcoming.length > 0 && (
        <HomeNextExhibitionBanner upcoming={exhibitions.upcoming} />
      )}
      <HomeAnnouncementBar />
    </div>

    {/* Hero Banner Section */}
    <section className="hero-banner hero-banner--bleed">
      <div className="hero-content">
        <h1 className="hero-title">{site.hero_title || SITE_SETTINGS_DEFAULTS.hero_title}</h1>
        <p className="hero-subtitle">{site.hero_subtitle || SITE_SETTINGS_DEFAULTS.hero_subtitle}</p>
        <p className="hero-description">{site.hero_description || SITE_SETTINGS_DEFAULTS.hero_description}</p>
        <StorefrontHeroActions
          placement="home"
          className="hero-actions d-flex flex-column flex-sm-row align-items-center justify-content-center gap-3"
          giftFinderPlacement="home"
          giftFinderClassName="btn btn-outline-secondary btn-lg hero-secondary-cta"
          primaryBtnClass="btn btn-primary btn-lg hero-cta"
          secondaryBtnClass="btn btn-outline-secondary btn-lg hero-secondary-cta"
        />
        {!loading && categories.length === 0 && (
          <p className="small text-white-50 text-center mt-2 mb-0">Connect MySQL and add products in Admin to load categories.</p>
        )}
      </div>
    </section>

    <HomeExhibitions upcoming={exhibitions.upcoming} recent={exhibitions.recent} status={exhibitions.status} />

    <HomeDynamicCatalog />

    <section className="about-brand">
      <div className="container store-page__container">
        <div className="about-card">
          <div className="row align-items-center">
            <div className="col-md-4">
              <div className="about-image-wrapper">
                <img
                  src="/saloni-logo.png"
                  alt="Saloni Designs"
                  className="about-image about-image--logo"
                  width={200}
                  height={200}
                />
              </div>
            </div>
            <div className="col-md-8">
              <h2 className="about-title handwritten">
                {site.home_about_title || SITE_SETTINGS_DEFAULTS.home_about_title}
              </h2>
              <p className="about-text">{site.home_about_text || SITE_SETTINGS_DEFAULTS.home_about_text}</p>
              <p className="about-tagline handwritten">
                &quot;{site.home_about_tagline || SITE_SETTINGS_DEFAULTS.home_about_tagline}&quot;
              </p>
              <Link to="/about" className="btn btn-secondary">Learn More About Us</Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Blog & Styling Stories Section */}
    <section className="blog-teasers">
      <div className="container store-page__container">
        <div className="section-header text-center mb-4">
          <h2 className="section-title">{blogContent.section_title}</h2>
          <p className="section-subtitle">{blogContent.section_subtitle}</p>
        </div>
        <div className="row g-4 align-items-stretch blog-teasers-row">
          {blogStories.map((story) => (
            <div className="col-md-4 d-flex" key={story.link}>
              <div className="blog-card w-100">
                <div className="blog-card-body">
                  <h3 className="blog-card-title">{story.title}</h3>
                  <p className="blog-card-text">{story.excerpt}</p>
                  <Link to={story.link} className="blog-card-link">Read more</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Instagram Feed Section */}
    <section className="instagram-feed">
      <div className="container store-page__container">
        <div className="section-header text-center mb-4">
          <h2 className="section-title">{homeCopy.instagram_title}</h2>
          <p className="section-subtitle">{homeCopy.instagram_subtitle}</p>
        </div>
        {String(site.instagram_embed_html || '').trim() ? (
          <InstagramEmbed html={site.instagram_embed_html} />
        ) : instagramPosts.length === 0 ? (
          <p className="text-center text-muted">{homeCopy.instagram_empty}</p>
        ) : (
          <div className="instagram-grid">
            {instagramPosts.map((item) => (
              <div className="instagram-card" key={item.key}>
                <CatalogImage src={item.image} alt={item.caption} className="instagram-image" />
                <p className="instagram-caption">{item.caption}</p>
              </div>
            ))}
          </div>
        )}
        <div className="text-center mt-4">
          <a href={site.instagram_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary">{homeCopy.instagram_button}</a>
        </div>
      </div>
    </section>
  </div>
  );
};

export default Home;
